<template>
  <div class="flow mx-auto p-6">
    <div
      v-if="flow && currentQuestion && !loadingFinalPageData"
    >
      <KButton id="backToSelectionButton" text-only @click="handleBackButtonClick">
        <template #prepend>
          <KIcon name="keyboard-arrow-left" class="h-3 w-2 mr-4" />
        </template>
        {{ flow.backToSelectionText }}
      </KButton>
      <section class="flow__container" :class="[{ 'md:-mt-4': progress }]">
        <div v-if="progress" class="flow__progress">
          <p :id="$id('progress')">{{ flow.stepHeaderText }} {{progress.currentStep}}/{{progress.totalSteps}}</p>
          <ProgressBar :aria-labelledby="$id('progress')" :min="0" :max="progress.totalSteps" :value="progress.currentStep"/>
        </div>
        <h1 :id="$id()" class="flow__heading" role="alert">{{ currentQuestion.heading }}</h1>
        <div class="richtext mb-6" v-dompurify-html="currentQuestion.description" role="alert" />
        <div v-if="progress?.currentStep === 1 && !selection.length && initialDeclineAmount !== 0" class="flow__estimate">{{ global.declineBalanceContent.declineBalanceLabel }}
          <KInformationToolTip :alignPopUpAtTop=true :disclaimerText="global.declineBalanceContent.declineBalanceTooltip"/>
          <KIcon v-if="isLoadingEstimatedCost" name="dots" class="inline h-5 w-10 object-cover text-primary"/>
          <span v-else class="text-primary">
            ${{ initialDeclineAmount }}
          </span>
        </div>
        <div v-if="allowanceAmount !== 0" class="flow__estimate">{{ global.globalAllowanceContent.globalAllowanceLabel }}
          <KInformationToolTip :alignPopUpAtTop=true :disclaimerText="global.globalAllowanceContent.globalAllowanceTooltip"/>
          <KIcon v-if="isLoadingEstimatedCost" name="dots" class="inline h-5 w-10 object-cover text-primary"/>
          <span v-else class="text-primary">
            ${{ allowanceAmount }}
          </span>
        </div>
        <div v-if="startDeclineAmount !== 0" class="flow__estimate">{{ global.declineBalanceContent.declineBalanceLabel }}
          <KInformationToolTip :alignPopUpAtTop=true :disclaimerText="global.declineBalanceContent.declineBalanceTooltip"/>
          <KIcon v-if="isLoadingEstimatedCost" name="dots" class="inline h-5 w-10 object-cover text-primary"/>
          <span v-else class="text-primary">
            ${{ remainingDeclineAmount }}
          </span>
        </div>
        <div class="flow__estimate">{{ flow.estimatedOutOfPocketText }}
          <KIcon v-if="isLoadingEstimatedCost" name="dots" class="inline h-5 w-10 object-cover text-primary"/>
          <span v-else class="text-primary">
            ${{ estimatedOutOfPocketCost }}
          </span>
        </div>
        <SelectionGroup
          :options="currentQuestion.answers"
          :aria-labelledby="$id()"
          v-model="selection"
          :multiple="currentQuestion.isMultipleChoice"
        />
      </section>
      <KButton
        theme="primary"
        class="mx-auto"
        :disabled="!selection.length || isLoadingEstimatedCost"
        @click="handleNextButtonClick"
        :isLoading="isLoadingNextQuestion"
        >{{ flow.nextButtonText }}</KButton
      >
    </div>
    <br />
    <div v-if="allowanceAmount !== 0" class="text-xs text-center md:block" v-dompurify-html="global.globalAllowanceContent.globalAllowanceDisclaimer" />
    <div v-if="startDeclineAmount !== 0" class="text-xs text-center md:block" v-dompurify-html="global.declineBalanceContent.declineBalanceDisclaimer" />

    <!-- Loading final page screen -->
    <div v-if="flow && loadingFinalPageData">
      <section class="flow__container">
        <div class="flow__progress">
          <p> {{ loadingScreen.completeText }} </p>
        </div>
        <h1 class="flow__heading"> {{ loadingScreen.loadingText }} </h1>
        <div class="flow__estimate">{{ flow.estimatedOutOfPocketText }}
          <span class="text-primary">
            ${{ estimatedOutOfPocketCost }}
          </span>
        </div>
        <KLoading />
      </section>
    </div>
  </div>
</template>

<script lang="ts">
import { useFlow, useGlobal } from '@/store'
import { defineComponent, computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useRoute, useRouter } from 'vue-router'
import {
  KButton,
  SelectionGroup,
  KIcon,
  ProgressBar,
  KLoading,
  KInformationToolTip
} from '@/components'
import { Selection } from '@/model'
import { Service } from '@/types/ServicePackage'
import { trackEvent, trackPage } from '@/utils/analytics'
import { addAdobeDataLayerPageView, addAdobeDataLayerEventBack } from '@/utils/adobe-analytics-data-layer'

export default defineComponent({
  name: 'Flow',
  components: { KButton, SelectionGroup, KIcon, ProgressBar, KLoading, KInformationToolTip },
  setup () {
    const route = useRoute()
    const router = useRouter()
    const flowState = useFlow()
    const global = useGlobal()

    const selection = ref([] as Selection[])
    const isLoadingNextQuestion = ref(false)
    const isLoadingEstimatedCost = ref(false)

    const {
      flow,
      currentQuestion,
      progress,
      hasPreviousQuestion,
      selectionHistory,
      loadingScreen,
      estimatedOutOfPocketCost: _estimatedOutOfPocketCost,
      startDeclineAmount: _startDeclineAmount,
      initialDeclineAmount,
      initialAllowanceAmount,
      remainingDeclineAmount: _remainingDeclineAmount,
      allowanceAmount: _allowanceAmount
    } = storeToRefs(flowState)

    const {
      startFlow,
      goToNextQuestion,
      goToPreviousQuestion,
      updateEstimatedOutOfPocketPriceMap
    } = flowState

    const {
      loadingFinalPageData
    } = storeToRefs(global)

    const estimatedOutOfPocketCost = computed(() => {
      return _estimatedOutOfPocketCost.value(selection.value as Selection[])
    })
    const startDeclineAmount = computed(() => {
      return _startDeclineAmount.value(selection.value as Selection[])
    })
    const remainingDeclineAmount = computed(() => {
      return _remainingDeclineAmount.value(selection.value as Selection[])
    })

    const allowanceAmount = computed(() => {
      return _allowanceAmount.value(selection.value as Selection[])
    })

    watch(() => currentQuestion?.value, (currentQuestion) => {
      if (!currentQuestion) {
        isLoadingEstimatedCost.value = true
        return startFlow()
          .finally(() => { isLoadingEstimatedCost.value = false })
      }
      selection.value = selectionHistory.value[currentQuestion.uid]?.selections || []
      // addAdobeDataLayerPageView()
      // trackPage(currentQuestion.title)
    }, { immediate: true })

    watch(() => selection.value, async (newSelection) => {
      if (newSelection.length > 1) {
        isLoadingEstimatedCost.value = true
        await updateEstimatedOutOfPocketPriceMap(newSelection.filter(selection => selection.service).map((selection) => selection.service as Service), true, true)
        isLoadingEstimatedCost.value = false
      }
    })

    const handleNextButtonClick = async () => {
      if (isLoadingNextQuestion.value || isLoadingEstimatedCost.value) {
        return
      }
      const elem = document.getElementById('backToSelectionButton')
      if (elem) {
        elem.focus()
        elem.blur()
      }
      isLoadingNextQuestion.value = true
      if (await goToNextQuestion(selection.value as Selection[])) {
        isLoadingNextQuestion.value = false
        return
      }

      await global.getFinalPageData()

      if (!global.isErrorModalOpened) {
        router.replace({ path: 'final', query: route.query })
      }
    }

    const handleBackButtonClick = () => {
      addAdobeDataLayerEventBack()
      if (hasPreviousQuestion.value) {
        goToPreviousQuestion()
        return
      }
      router.back()
    }

    return {
      flow,
      currentQuestion,
      isLoadingNextQuestion,
      isLoadingEstimatedCost,
      selection,
      progress,
      loadingFinalPageData,
      startDeclineAmount,
      remainingDeclineAmount,
      initialDeclineAmount,
      initialAllowanceAmount,
      global,
      loadingScreen,
      estimatedOutOfPocketCost,
      allowanceAmount,
      handleNextButtonClick,
      handleBackButtonClick
    }
  }
})
</script>

<style>
.flow {
  &__container {
    @apply  text-center mb-8;
  }

  &__progress {
    @apply max-w-md mt-6 mx-auto;

    p {
      @apply mb-4 uppercase text-sm font-bold;
    }
  }

  &__heading {
    @apply  mt-8 mb-4 font-bold text-xl;
  }

  &__estimate {
    @apply font-bold;
  }
}

@screen md {
  .flow {
    &__progress {
      @apply mt-0;

      p {
        @apply mb-6;
      }
    }

    &__heading {
      @apply text-2xl mt-10;
    }

    &__estimate {
      font-size: 22px;
    }
  }
}
</style>
